import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { RaButtonComponent, RaLoadingComponent, RaToastService } from '@shared/components';
import { CardTypeFormatPipe, FileSizePartsPipe } from '@shared/pipes';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DocumentItemTypeEnum } from '@domain';
import { concatMap, from } from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { FavoriteFieldController } from 'src/app/domain/controllers/favorite-field.controller';
import { FavoriteFieldEntity } from 'src/app/domain/entities/favorite-field.entity';
import { DocumentItemPartial } from 'src/app/domain/entities/partials/documents/document-item.partial';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { EditDataService } from '../../../services/_edit-data.service';
import { MenuOptionsService } from '../../../services/menu-options.service';
import { CardTypes } from '../../cards/_card-base/enums/card-types.enum';
import { CalloutCardComponent } from '../../cards/callout-card/callout-card.component';
import { StatsCardComponent } from '../../cards/stats-card/stats-card.component';
import { TableCardComponent } from '../../cards/table-card/table-card.component';
import { SemaphoreCardComponent } from '../../cards/semaphore-card/semaphore-card.component';
import { ChartCardComponent } from '../../cards/chart-card/chart-card.component';
import { GroupImageCardComponent } from '../../cards/group-image-card/group-image-card.component';
import { ImageCardComponent } from '../../cards/image-card/image-card.component';
import { TextCardComponent } from '../../cards/text-card/text-card.component';
import { VideoCardComponent } from '../../cards/video-card/video-card.component';

/** Agrupamento de tipos usado no filtro lateral e nas cores dos chips. */
type FieldGroup = 'text' | 'image' | 'video';
type TypeFilter = 'all' | FieldGroup;

@Component({
    selector: 'modal-list-favorite-fields',
    templateUrl: 'modal-list-favorite-fields.component.html',
    styleUrl: 'modal-list-favorite-fields.component.scss',
    imports: [
        NgClass,
        RaModalComponent,
        RaLoadingComponent,
        RaEmptyComponent,
        RaButtonComponent,
        RaIconComponent,
        TextCardComponent,
        ImageCardComponent,
        VideoCardComponent,
        GroupImageCardComponent,
        CalloutCardComponent,
        StatsCardComponent,
        TableCardComponent,
        SemaphoreCardComponent,
        ChartCardComponent,
        CardTypeFormatPipe,
        FileSizePartsPipe,
        TranslatePipe,
    ],
})
export class ModalListFavoriteFieldsComponent implements OnInit {
    public close = output<void>();
    public loading = signal(false);
    public loadingAddFavoriteField = signal(false);
    public favoriteFields = signal<FavoriteFieldEntity[]>([]);
    // Seleção múltipla: o filtro da esquerda só filtra; selecionar é no card.
    public selectedIds = signal<Set<string>>(new Set());
    public selectedCount = computed(() => this.selectedIds().size);
    public typeFilter = signal<TypeFilter>('all');
    public cardTypes = CardTypes;

    /** Cards visíveis segundo o filtro de tipo (a seleção não se perde ao filtrar). */
    public filteredFavoriteFields = computed(() => {
        const filter = this.typeFilter();
        if (filter === 'all') return this.favoriteFields();
        return this.favoriteFields().filter(f => this.fieldGroup(f.field) === filter);
    });

    /** Só oferece filtros de tipos que existem na lista. */
    public availableFilters = computed<{ group: FieldGroup; count: number }[]>(() => {
        const counts = new Map<FieldGroup, number>();
        for (const favoriteField of this.favoriteFields()) {
            const group = this.fieldGroup(favoriteField.field);
            counts.set(group, (counts.get(group) ?? 0) + 1);
        }

        return (['text', 'image', 'video'] as FieldGroup[])
            .filter(group => counts.has(group))
            .map(group => ({ group, count: counts.get(group)! }));
    });

    // * [Injects]
    private _i18n = inject(TranslateService);
    private _raToastService = inject(RaToastService);
    private _editDataService = inject(EditDataService);
    private _menuOptionsService = inject(MenuOptionsService);
    private _favoriteFieldController = inject(FavoriteFieldController);
    private _documentFieldsController = inject(DocumentFieldsController);

    ngOnInit(): void {
        this.getFavoriteFields();
    }

    /** Mesmo mapa de ícones por tipo do card-base. */
    public typeIcon(item: DocumentItemPartial): string {
        switch (item.type) {
            case DocumentItemTypeEnum.SUBTITLE:
                return 'smallcaps';
            case DocumentItemTypeEnum.IMAGE:
                return 'gallery';
            case DocumentItemTypeEnum.GROUP_IMAGE:
                return 'grid-2';
            case DocumentItemTypeEnum.VIDEO:
            case DocumentItemTypeEnum.GROUP_VIDEO:
                return 'video-play';
            default:
                return 'text';
        }
    }

    /** Família do campo: texto (inclui títulos), imagem ou vídeo (inclui grupos). */
    public fieldGroup(item: DocumentItemPartial): FieldGroup {
        switch (item.type) {
            case DocumentItemTypeEnum.IMAGE:
            case DocumentItemTypeEnum.GROUP_IMAGE:
                return 'image';
            case DocumentItemTypeEnum.VIDEO:
            case DocumentItemTypeEnum.GROUP_VIDEO:
                return 'video';
            default:
                return 'text';
        }
    }

    /** Ícone e rótulo representativos de cada grupo do filtro. */
    public groupIcon(group: FieldGroup): string {
        switch (group) {
            case 'image':
                return 'gallery';
            case 'video':
                return 'video-play';
            default:
                return 'text';
        }
    }

    public groupLabel(group: FieldGroup): string {
        return `common.cardType.${group}`;
    }

    public isSelected(id: string): boolean {
        return this.selectedIds().has(id);
    }

    public toggleSelection(favoriteField: FavoriteFieldEntity, event: Event): void {
        // Controles do vídeo continuam funcionais: play/pause não muda a seleção.
        if (event.target instanceof HTMLElement && event.target.closest('video')) return;
        event.preventDefault();

        const ids = new Set(this.selectedIds());
        if (ids.has(favoriteField._id)) ids.delete(favoriteField._id);
        else ids.add(favoriteField._id);
        this.selectedIds.set(ids);
    }

    private getFavoriteFields(): void {
        this.loading.set(true);
        this._favoriteFieldController.getAll().subscribe({
            next: favoriteFields => {
                this.loading.set(false);
                this.favoriteFields.set(favoriteFields);
            },
            error: () => this.loading.set(false),
        });
    }

    public addSelectedFavoriteFields(): void {
        if (this.loadingAddFavoriteField()) return;

        // Ordem da lista, não da seleção: o resultado no documento fica previsível.
        const selected = this.favoriteFields().filter(f => this.selectedIds().has(f._id));
        if (selected.length === 0) return;

        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        const sessionId = this._editDataService.getCurrentSessionId();
        if (!sessionId) return;

        const aboveFieldId = this._menuOptionsService.aboveFieldId();
        const belowFieldId = this._menuOptionsService.belowFieldId();
        // Se estiver dentro de uma pasta, os favoritos entram nela.
        const folderItemId = this._editDataService.currentFolderId() ?? undefined;

        // Com âncora "abaixo de X" cada inserção entra logo após X, o que
        // inverteria a ordem — enviar de trás pra frente compensa.
        const ordered = belowFieldId ? [...selected].reverse() : selected;

        this.loadingAddFavoriteField.set(true);

        // Sequencial de propósito: cada inserção respeita o compare-and-set
        // (rev) da edição concorrente; em paralelo as revs colidiriam.
        from(ordered)
            .pipe(
                concatMap(favoriteField =>
                    this._documentFieldsController.addFavoriteField(
                        documentId,
                        sessionId,
                        favoriteField._id,
                        aboveFieldId,
                        belowFieldId,
                        folderItemId,
                        false
                    )
                )
            )
            .subscribe({
                complete: () => {
                    this.loadingAddFavoriteField.set(false);
                    this._raToastService.success({
                        message:
                            selected.length === 1
                                ? this._i18n.instant(
                                      'document.edit.listFavoriteFields.fieldAdded'
                                  )
                                : this._i18n.instant(
                                      'document.edit.listFavoriteFields.fieldsAdded',
                                      { count: selected.length }
                                  ),
                    });
                    this.close.emit();
                },
                // O erro interrompe a sequência; o controller já mostra o toast
                // de erro e o modal permanece aberto para tentar de novo.
                error: () => this.loadingAddFavoriteField.set(false),
            });
    }
}
